import React, { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import "./App.css";
import { useAuth } from "./context/AuthContext";
import ProtectedRoutes from "./pages/ProtectedRoutes";
import Header from "./components/Header";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Lazy-loaded pages
const Home = React.lazy(() => import("./pages/Home"));
const Login = React.lazy(() => import("./pages/Login"));
const ActivatePage = React.lazy(() => import("./pages/Activate"));
const SchedulePage = React.lazy(() => import("./pages/Schedule"));
const DashboardPage = React.lazy(() => import("./pages/Dashboard"));
const AddProject = React.lazy(() => import("./pages/AddProjectPage"));
const IssueManagementPage = React.lazy(() => import("./pages/IssueManagement"));
const FilesPage = React.lazy(() => import("./pages/Files"));
const Team = React.lazy(() => import("./pages/Team"));
const SettingsPage = React.lazy(() => import("./pages/Settings"));
const ReferenceQRPage = React.lazy(() => import("./pages/Reference_QR"));

const PROTECTED_ROUTES = [
  { path: "/homepage", element: <Home /> },
  { path: "/schedule/:projectId", element: <SchedulePage /> },
  { path: "/issues/:projectId", element: <IssueManagementPage /> },
  { path: "/dashboard/:projectId", element: <DashboardPage /> },
  { path: "/files", element: <FilesPage /> },
  { path: "/members", element: <Team /> },
  { path: "/addproject", element: <AddProject /> },
  { path: "/settings/:projectId", element: <SettingsPage /> },
  { path: "/reference-qr/:projectId", element: <ReferenceQRPage /> },
];

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  const hideHeader = ["/login", "/register", "/activate"].includes(location.pathname);
  const showLayout = !hideHeader && isAuthenticated;

  if (loading) {
    return <div style={{ textAlign: "center", marginTop: "30vh" }}>Loading...</div>;
  }

  return (
    <>
      {showLayout && <Header />}

      <Box component="main" sx={getMainStyles(showLayout)}>
        <Suspense fallback={<div style={{ textAlign: "center", marginTop: "20vh", width: "100%" }}>Loading...</div>}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/activate" element={<ActivatePage />} />

            <Route element={<ProtectedRoutes />}>
              {PROTECTED_ROUTES.map(({ path, element }) => (
                <Route key={path} path={path} element={element} />
              ))}
            </Route>

            <Route
              path="/"
              element={<Navigate to={isAuthenticated ? "/homepage" : "/login"} replace />}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Box>

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
    </>
  );
}

const getMainStyles = (showLayout) => ({
  marginLeft: showLayout ? { xs: 0, md: "var(--sidebar-width, 220px)" } : 0,
  marginTop: showLayout ? "64px" : 0,
  transition: "margin 200ms ease",
  minHeight: showLayout ? "calc(100vh - 64px)" : "100vh",
  height: showLayout ? "calc(100vh - 64px)" : "auto",
  display: showLayout ? "flex" : "block",
  flexDirection: showLayout ? "column" : "unset",
  background: "var(--surface-page, #fafafa)",
  p: 0,
  boxSizing: "border-box",
});

export default function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}